import { FilterFacets, ParameterValue } from '@/study-search/domain';
import { ParameterType, SortingType } from '@/study-search/enums';
import { AttendanceFilterOptionValue, FacetOptionValue } from '@studyportals/search-filters';
import { DataStorage } from '@studyportals/data-storage';

import SearchResultsPresenter from '@/study-search/presenters/SearchResultsPresenter';
import getters from '@/study-search/store/getters';
import { FaqHandler } from './FaqHandler';
import { IOnlineProgrammeCount } from '@/study-search/interfaces';
import { PortalType } from '@studyportals/domain-client';
import store from '@/study-search/store';

const UNSUPPORTED_PARAMETERS = [ParameterType.CURRENCY, ParameterType.USER_COUNTRY];
export abstract class FaqFacetsHandler extends FaqHandler {
	protected abstract dataStorageKey: string;

	protected async getFacets(parameters: ParameterValue[]): Promise<FilterFacets[]> {
		const cleanParameters = this.clearUnsupportedParameters(parameters);

		if (!this.shouldMakeAPIRequest(cleanParameters)) {
			return store.getters.getAllFacets();
		}

		const portalType = getters.getPortalType();
		const { facets } = await SearchResultsPresenter.retrieveSearchResults(
			parameters,
			portalType,
			0,
			0,
			SortingType.RELEVANCE,
			[FacetOptionValue.EDUCATIONAL_LEVEL, FacetOptionValue.ATTENDANCES]
		);

		return facets ?? [];
	}

	private clearUnsupportedParameters(parameters: ParameterValue[]): ParameterValue[] {
		return [...parameters.filter((_) => !UNSUPPORTED_PARAMETERS.includes(_.type))];
	}

	private shouldMakeAPIRequest(parameters: ParameterValue[]): boolean {
		const storeFacets = store.getters.getAllFacets();
		const hasRequestedParameters = parameters.some((p) => storeFacets.some((f) => f.type === p.type));

		return !hasRequestedParameters;
	}

	protected getOnlineProgrammeCount(facets: FilterFacets[]): number {
		const attendanceFacets = facets.find((_) => _.type === ParameterType.DELIVERY_METHOD);
		if (!attendanceFacets) {
			return 0;
		}

		const onlineFacet = attendanceFacets.values.find(
			(_) => (_.identity as AttendanceFilterOptionValue) === AttendanceFilterOptionValue.ONLINE
		);
		const count = onlineFacet?.count ?? 0;

		return count;
	}

	protected getPercentageOfOnlineStudies(facets: FilterFacets[], onlineProgrammes: number): string {
		const educationLevelFacets = facets.find((_) => _.type === ParameterType.EDUCATION_LEVEL);
		if (!educationLevelFacets) {
			return '0';
		}

		const portalType = getters.getPortalType();
		const facetValue = educationLevelFacets.values.find((_) => (_.identity as PortalType) === portalType);

		if (!facetValue) {
			return '0';
		}

		const percentage = (onlineProgrammes / facetValue?.count) * 100;
		return percentage.toFixed(0);
	}

	protected storeOnlineProgrammeData(data: IOnlineProgrammeCount): void {
		DataStorage.store(this.dataStorageKey, JSON.stringify(data), 300);
	}

	protected getFromDataStorage(): IOnlineProgrammeCount | undefined {
		const data = DataStorage.retrieve(this.dataStorageKey) as string;
		if (!data) {
			return;
		}

		return JSON.parse(data) as IOnlineProgrammeCount;
	}

	protected getHostname(): string | undefined {
		return window['PortalMap'] ? (window['PortalMap'].master as string) : undefined;
	}

	protected abstract getFacetsForFilterCombination(): Promise<FilterFacets[]>;

	protected getSearchPageOnlineProgrammesUrl(suffix = ''): string {
		const portalType = getters.getPortalType();

		return `/search/${portalType}/online${suffix}`;
	}
}
